export type BatchIteratorProgressParams = {
  processedItems: number,
  totalItems: number,
  progress: number
}

export type BatchIteratorOptions = {
  onProgress?: (e: BatchIteratorProgressParams) => any
  batchSize?: number
};

const baseLoaderOptions: BatchIteratorOptions = {
  onProgress: e => console.log(JSON.stringify(e)),
  batchSize: 15,
};

export async function batchIterator<T, TR>(items: T[], consumer: (item: T, index: number) => Promise<TR> | TR, options?: BatchIteratorOptions): Promise<{
  result: TR[],
  items: T[],
}> {
  const batchSize = options?.batchSize || baseLoaderOptions.batchSize as number;
  const onProgress = options?.onProgress || baseLoaderOptions.onProgress as (e: BatchIteratorOptions) => any;

  const progress: BatchIteratorProgressParams = {
    totalItems: items.length,
    processedItems: 0,
    progress: 0,
  };

  onProgress(progress);

  const result = new Array(items?.length) as TR[];

  function* getNextItem<T>(items: T[]) {
    let index = 0;
    while (index < items.length) {
      yield { index, item: items[index] };
      index++;
    }
  }

  const iterator = getNextItem(items);

  const createNewTask = async () => {
    const next = iterator.next();
    if (!next.done) {
      result[next.value.index] = await consumer(next.value.item, next.value.index);
      progress.processedItems++;
      progress.progress = progress.processedItems / progress.totalItems;
      onProgress(progress);
      await createNewTask();
    }
  };

  for (let i = 0; i < batchSize; i++) {
    createNewTask();
  }

  await new Promise(async resolve => {
    while (progress.processedItems !== progress.totalItems) {
      await new Promise(res => setTimeout(res, 50));
    }
    resolve(true);
  });

  progress.progress = 1;
  onProgress(progress);

  return {
    items,
    result,
  };
}

export type RunParallelOptions = {
  maxParallelism?: number,
  mode?: "fixed" | "flexible"
}

export async function runParallel<T, R>(array: T[], apply: (v: T, i: number) => R | Promise<R>, options?: RunParallelOptions) {
  if (array && array.length) {
    if (options?.mode === "flexible") {
      const result: R[] = [];

      await batchIterator(array, apply, {
        batchSize: options?.maxParallelism,
      });

      return result;
    } else {
      const chunks: T[][] = [];
      const chunkSize = options?.maxParallelism ?? 5;
      for (let i = 0; i < array.length; i += chunkSize) {
        const chunk = array.slice(i, i + chunkSize);
        chunks.push(chunk);
      }

      const result: R[] = [];
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        result.push(...(await Promise.all(chunk.map(async (value, index) => {
          try {
            return await apply(value, (i * chunkSize) + index);
          } catch (error) {
            console.error(error);
            return undefined as R;
          }
        }))));
      }
      return result;
    }
  }
  return [];
}